/**
 * EditMessageDialog - Diálogo para edição de mensagens
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ConversationMessage, MessageContent } from '@troia-v3/types';
import { Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
interface EditMessageDialogProps {
  message: ConversationMessage;
  isOpen: boolean;
  onClose: () => void;
  onSave: (newContent: MessageContent[]) => void;
  isLoading?: boolean;
}

const EditMessageDialog: React.FC<EditMessageDialogProps> = ({
  message,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [editedText, setEditedText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && message) {
      // Extract text content from message
      const textContent = message.content.find(
        (c) => c.type === 'text' && 'text' in c
      );
      setEditedText(textContent && 'text' in textContent ? textContent.text : '');

      // Focus textarea after render
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }, 100);
    }
  }, [isOpen, message]);

  const handleSave = () => {
    const trimmedText = editedText.trim();
    if (!trimmedText) {
      return;
    }

    // Build new content array
    const newContent: MessageContent[] = [
      {
        type: 'text',
        text: trimmedText,
      },
    ];

    onSave(newContent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      {/* Dialog */}
      <div
        className="relative rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full sm:mx-4 overflow-hidden max-h-[92vh]"
        style={{
          backgroundColor: '#ffffff',
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
              Editar mensagem
            </h3>
            {message.isEdited && (
              <span className="text-xs italic" style={{ color: '#64748b' }}>
                (editada anteriormente)
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded transition-colors disabled:opacity-50"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="mb-4">
            <label
              htmlFor="edit-message-text"
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Texto da mensagem
            </label>
            <Textarea
              ref={textareaRef}
              id="edit-message-text"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              placeholder="Digite o texto da mensagem..."
              rows={4}
              resize="none"
              style={{
                minHeight: '120px',
                maxHeight: '300px',
              }}
            />
            {editedText.trim().length === 0 && (
              <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
                <Icon name="alert-circle" className="w-4 h-4" />
                <span>A mensagem não pode estar vazia</span>
              </div>
            )}
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
              Editável por até 15 minutos após o envio.
            </div>
          </div>

          <div className="text-xs space-y-1" style={{ color: '#64748b' }}>
            <p>• Pressione <kbd className="px-1.5 py-0.5 border rounded text-xs" style={{ backgroundColor: '#f1f5f9', borderColor: '#e5e7eb' }}>Enter</kbd> para salvar</p>
            <p>• Pressione <kbd className="px-1.5 py-0.5 border rounded text-xs" style={{ backgroundColor: '#f1f5f9', borderColor: '#e5e7eb' }}>Shift + Enter</kbd> para nova linha</p>
            <p>• Pressione <kbd className="px-1.5 py-0.5 border rounded text-xs" style={{ backgroundColor: '#f1f5f9', borderColor: '#e5e7eb' }}>Esc</kbd> para cancelar</p>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{
            backgroundColor: '#f1f5f9',
            borderColor: '#e5e7eb',
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: '#1e293b',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || editedText.trim().length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: '#8b5cf6',
            }}
            onMouseEnter={(e) => {
              if (!isLoading && editedText.trim().length > 0) {
                e.currentTarget.style.backgroundColor = '#7c3aed';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#8b5cf6';
            }}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <Icon name="check" className="w-4 h-4" />
                <span>Salvar alterações</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditMessageDialog;
